import { authFetch } from './api';

/**
 * Ported from `apr_template/src/services/aiKnowledgeApi.ts`.
 *
 * THE ONE DEVIATION: the source builds these calls on an axios instance with a bearer
 * token from localStorage. This uses `fetch` through the platform's `authFetch`, which
 * carries the session cookie instead. Adding axios for one file was declined, and the
 * data-access seam is on §1.2's allowed list either way.
 *
 * Everything the SCREEN sees is unchanged — the exported function names, their
 * arguments, their return shapes and the `KnowledgeFile` / `KnowledgeFileDetail` types
 * are the source's, verbatim. `KnowledgeLibrary.tsx` imports this without knowing the
 * transport changed, which is the point.
 *
 * The source's 401 interceptor is not reproduced: the platform's shell already handles
 * an expired session centrally, and a second redirect would race it.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface KnowledgeFile {
  id: string;
  file_name: string;
  mime_type?: string;
  size_bytes: number;
  description?: string;
  application_id?: string | null;
  application_name?: string | null;
  /** Document category: 'diagrams' | 'licensing' (null = uncategorised). */
  category?: string | null;
  tags: string[];
  text_extraction_status?: string;
  uploaded_by_name?: string;
  uploaded_at: string;
}

/** One file, including the extracted text — what the AI actually reads. */
export interface KnowledgeFileDetail extends KnowledgeFile {
  extracted_text?: string | null;
  text_extraction_error?: string | null;
}

async function jsonOrThrow(res: Response) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * List knowledge files with optional scope / tag filters.
 * applicationId === 'portfolio' → portfolio-wide files only
 * applicationId === undefined   → all files
 * applicationId === <id>        → that application's files
 */
export async function listKnowledgeFiles(opts?: {
  applicationId?: string;
  tag?: string;
  category?: string;
}): Promise<KnowledgeFile[]> {
  const params = new URLSearchParams();
  if (opts?.applicationId !== undefined) params.append('applicationId', opts.applicationId);
  if (opts?.tag) params.append('tag', opts.tag);
  if (opts?.category) params.append('category', opts.category);
  const qs = params.toString();
  return jsonOrThrow(await authFetch(`/api/ai/knowledge${qs ? `?${qs}` : ''}`));
}

/**
 * Upload files. The caller is responsible for constructing the FormData
 * with the `files` field (multiple) plus optional `applicationId`,
 * `description`, and `tags` (comma-separated string).
 */
export async function uploadKnowledgeFiles(formData: FormData): Promise<KnowledgeFile[]> {
  // `authFetch` omits Content-Type for a FormData body so the browser can set the
  // multipart boundary itself. Passing `headers: {}` here did NOT achieve that — it
  // spread into the default and left application/json in place.
  return jsonOrThrow(await authFetch('/api/ai/knowledge', { method: 'POST', body: formData }));
}

/** Download a file and return its Blob plus the suggested filename. */
export async function downloadKnowledgeFile(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await authFetch(`/api/ai/knowledge/${id}/download`);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  // Extract filename from Content-Disposition header if available
  const disposition = res.headers.get('content-disposition') || '';
  let filename = `knowledge-file-${id}`;
  const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
  if (match) {
    try {
      filename = decodeURIComponent(match[2]!);
    } catch {
      filename = match[2]!;
    }
  }
  return { blob: await res.blob(), filename };
}

/** Update description, tags, or application scope of a file. */
export async function updateKnowledgeFile(
  id: string,
  patch: { description?: string; tags?: string[]; application_id?: string | null },
): Promise<KnowledgeFile> {
  return jsonOrThrow(
    await authFetch(`/api/ai/knowledge/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  );
}

export async function getKnowledgeFile(id: string): Promise<KnowledgeFileDetail> {
  return jsonOrThrow(await authFetch(`/api/ai/knowledge/${id}`));
}

/** Delete a file (removes from DB and storage). */
export async function deleteKnowledgeFile(id: string): Promise<void> {
  const res = await authFetch(`/api/ai/knowledge/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Delete failed (${res.status})`);
}
